import { useState } from "react";
import { Button } from "@/components/ui/atoms/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/atoms/popover";

export type ConfirmPopoverProps = {
  /** Title shown in the confirmation dialog. */
  title: string;
  /** Optional description shown below the title. */
  description?: string;
  /** Label for the confirm button. @default "Confirm" */
  confirmLabel?: string;
  /** Label for the cancel button. @default "Cancel" */
  cancelLabel?: string;
  /** Button variant for the confirm action. @default "destructive" */
  confirmVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost";
  /** Called when the user confirms. */
  onConfirm: () => void;
  /** Trigger element — passed as Base UI render prop to merge with PopoverTrigger. */
  children: React.ReactElement;
  /** Side of the trigger to place the popover. @default "bottom" */
  side?: "top" | "bottom" | "left" | "right";
};

export function ConfirmPopover({
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "destructive",
  onConfirm,
  children,
  side = "bottom",
}: ConfirmPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger render={children} />
      <PopoverContent className="w-auto max-w-64 gap-3 p-4" side={side}>
        <PopoverHeader>
          <PopoverTitle>{title}</PopoverTitle>
          {description && (
            <PopoverDescription>{description}</PopoverDescription>
          )}
        </PopoverHeader>
        <div className="flex justify-end gap-2">
          <Button
            data-testid="confirm-popover-cancel"
            onClick={() => setOpen(false)}
            size="sm"
            variant="outline"
          >
            {cancelLabel}
          </Button>
          <Button
            data-testid="confirm-popover-confirm"
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
            size="sm"
            variant={confirmVariant}
          >
            {confirmLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
